import { formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';
import type { EstadoSimulado, FilaSimulada } from '../types/pricing-rules.types';

/**
 * Una fila simulada (o de la foto) dicha en palabras, para S4 y S10. Todo sale
 * de la fila: nada se recalcula en la pantalla.
 */

export const TEXTO_ESTADO: Record<EstadoSimulado, string> = {
  LIBRE: 'dentro del rango',
  LIMITADO: 'lo limita una regla',
  SIN_PRECIO_POSIBLE: 'conflicto entre reglas',
  // T8 (H15): no «falta un dato», que en Productos y en Hoy es «sin costo». La
  // causa de cada fila la dice `porQueDeLaFila` (el aviso del backend).
  SIN_DATOS: 'no se puede calcular',
  SIN_ESTRATEGIA: 'sin estrategia',
};

/** Los nombres de las reglas que limitaron, sacados de `rangos` (el id solo no le dice nada a nadie). */
export function nombresDeLimitantes(f: Pick<FilaSimulada, 'limitantes' | 'rangos'>): string[] {
  return f.limitantes.map(
    (id) => f.rangos.find((r) => r.ruleId === id)?.ruleName ?? 'una regla borrada'
  );
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : formatoPrecio(n));
/** «18 %», «12,5 %»: el porcentaje de un margen, sin ceros de más (pulido, spec 232). */
const pct = (n: number) =>
  formatoPorcentaje(n, { decimales: Number.isInteger(Math.round(n * 10) / 10) ? 0 : 1 });

/** «La estrategia pide 3.199; lo limita «No ganar menos de 20 %»». */
export function porQueDeLaFila(f: FilaSimulada): string {
  switch (f.estado) {
    case 'SIN_PRECIO_POSIBLE':
      return 'Las reglas no se pueden cumplir todas: te espera en Por decidir.';
    case 'SIN_DATOS':
      // El backend usa SIN_DATOS para varias causas (sin competencia, la estrategia
      // no decidió, pidió un precio imposible y, desde el quinto método, sin precio
      // de lista o sin costo) y manda la suya en `avisos`, escrita para una
      // persona: se dice ésa. Sin aviso, no se nombra una causa que puede no ser.
      return f.avisos[0]?.trim()
        ? f.avisos[0]
        : 'No se puede calcular: le falta competencia, costo o precio de lista.';
    case 'SIN_ESTRATEGIA':
      return 'Sin estrategia: no se mueve solo. Las reglas lo cuidan cuando alguien cambie su precio.';
    case 'LIMITADO': {
      const n = nombresDeLimitantes(f);
      const pide =
        f.origenDelPedido === 'ESTRATEGIA' ? `La estrategia pide ${fmt(f.precioPedido)}; ` : '';
      return `${pide}lo limita ${n.map((x) => `«${x}»`).join(' y ')}.`;
    }
    default:
      return f.origenDelPedido === 'ESTRATEGIA'
        ? f.precioPropuesto === f.precioHoy
          ? 'Dentro del rango: no cambia.'
          : 'Dentro del rango: queda lo que pide la estrategia.'
        : 'Dentro del rango.';
  }
}

/** «con su margen propio 18 %» / «la estrategia dice 30 %; vale el del producto» (S10). */
export function textoDeMargenPropio(
  f: Pick<FilaSimulada, 'usaMargenPropio' | 'margenPropio' | 'margenEstrategia'>
): {
  debajoDelPedido: string;
  enElEstado: string | null;
} | null {
  if (!f.usaMargenPropio || f.margenPropio == null) return null;
  return {
    debajoDelPedido: `con su margen propio ${pct(f.margenPropio)}`,
    enElEstado:
      f.margenEstrategia == null
        ? null
        : `la estrategia dice ${pct(f.margenEstrategia)}; vale el del producto`,
  };
}
